/**
 * Analytics event names for the Cerebra chatbot widget.
 *
 * We only track user *actions* / lifecycle events — never message content,
 * clarification answers, or feedback comment text. Properties are limited to
 * non-sensitive metadata (counts, ids, ratings, priorities).
 */
export class ChatbotTracking {
  static readonly TRACKER_CHATBOT_OPENED = '[Chatbot] opened';

  static readonly TRACKER_CHATBOT_CLOSED = '[Chatbot] closed';

  static readonly TRACKER_CHATBOT_NEW_CHAT = '[Chatbot] new chat started';

  static readonly TRACKER_CHATBOT_MESSAGE_SENT = '[Chatbot] message sent';

  static readonly TRACKER_CHATBOT_CLARIFICATION_ANSWERED = '[Chatbot] clarification answered';

  static readonly TRACKER_CHATBOT_FEEDBACK_SUBMITTED = '[Chatbot] feedback submitted';

  static readonly TRACKER_CHATBOT_ISSUE_RAISED = '[Chatbot] issue raised';

  static readonly TRACKER_CHATBOT_CONVERSATION_OPENED = '[Chatbot] conversation opened';

  static readonly TRACKER_CHATBOT_SUPPORT_HANDOFF = '[Chatbot] support handoff requested';

  static readonly TRACKER_CHATBOT_SUPPORT_MESSAGE_SENT = '[Chatbot] support message sent';

  static readonly TRACKER_CHATBOT_CSAT_ACKNOWLEDGED = '[Chatbot] CSAT acknowledged';

  /** User clicked a source/citation link under an AI answer. */
  static readonly TRACKER_CHATBOT_SOURCE_CLICKED = '[Chatbot] source clicked';

  // ── Reliability / health events ────────────────────────────────────────────

  /** A backend request failed — non-ok HTTP status, network failure, or SSE `error` event. */
  static readonly TRACKER_CHATBOT_API_ERROR = '[Chatbot] API error';

  /** An in-flight request was cancelled because the user navigated away (new chat,
   *  session switch, widget close/unmount, or leaving the closing conversation). */
  static readonly TRACKER_CHATBOT_REQUEST_ABORTED = '[Chatbot] request aborted';

  /** A backend request has not responded within {@link SLOW_RESPONSE_THRESHOLD_MS}. */
  static readonly TRACKER_CHATBOT_SLOW_RESPONSE = '[Chatbot] slow response';

  /** The AI service liveness probe failed on mount — the widget is shown but unusable. */
  static readonly TRACKER_CHATBOT_UNAVAILABLE = '[Chatbot] service unavailable';
}

/** A request open longer than this (ms) emits a `TRACKER_CHATBOT_SLOW_RESPONSE` event. */
export const SLOW_RESPONSE_THRESHOLD_MS = 10000;

/**
 * Emits a chatbot analytics event. Passed into the Cerebra config factories so the
 * config callbacks (the entry points for user actions) can report events without
 * depending on React hooks. Optional — the widget works with tracking disabled.
 */
export type ChatbotEventTracker = (event: string, properties?: Record<string, unknown>) => void;
